import { ControlPanel } from './ControlPanel';

/**
 * Wraps an existing ControlPanel with mobile-specific behavior:
 * collapsed by default, toggle button, overlay positioning, touch-friendly sizing.
 */
export class MobileControlPanel {
  private controlPanel: ControlPanel;
  private _isExpanded: boolean = false;
  private toggleButton: HTMLButtonElement;
  private styleElement: HTMLStyleElement;

  constructor(controlPanel: ControlPanel) {
    this.controlPanel = controlPanel;

    // Apply mobile-specific styles to the GUI DOM element
    Object.assign(this.controlPanel.gui.domElement.style, {
      position: 'fixed',
      top: '0',
      right: '0',
      maxWidth: '80vw',
      maxHeight: '100dvh',
      overflowY: 'auto',
      zIndex: '1000',
    });
    // iOS momentum scrolling (vendor-prefixed property requires bracket notation)
    (this.controlPanel.gui.domElement.style as unknown as Record<string, string>)['-webkit-overflow-scrolling'] = 'touch';

    // Hide panel by default on mobile
    this.controlPanel.gui.domElement.style.display = 'none';

    // Inject CSS overrides to ensure lil-gui elements meet 44×44px minimum touch target
    this.styleElement = document.createElement('style');
    this.styleElement.textContent = `
.lil-gui .controller {
  min-height: 44px;
}
.lil-gui .controller input,
.lil-gui .controller select,
.lil-gui .controller button,
.lil-gui .controller .slider {
  min-height: 44px;
  min-width: 44px;
}
.lil-gui .title {
  min-height: 44px;
  line-height: 44px;
}
`;
    document.head.appendChild(this.styleElement);

    // Create toggle button (gear icon) with minimum 44×44px touch target
    this.toggleButton = document.createElement('button');
    this.toggleButton.textContent = '⚙';
    Object.assign(this.toggleButton.style, {
      position: 'fixed',
      top: '10px',
      right: '10px',
      width: '44px',
      height: '44px',
      minWidth: '44px',
      minHeight: '44px',
      zIndex: '1001',
      border: 'none',
      borderRadius: '50%',
      background: 'rgba(0, 0, 0, 0.6)',
      color: 'white',
      fontSize: '24px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    });
    this.toggleButton.addEventListener('click', () => this.toggle());
    document.body.appendChild(this.toggleButton);
  }

  expand(): void {
    this.controlPanel.gui.domElement.style.display = '';
    this._isExpanded = true;
  }

  collapse(): void {
    this.controlPanel.gui.domElement.style.display = 'none';
    this._isExpanded = false;
  }

  toggle(): void {
    if (this._isExpanded) {
      this.collapse();
    } else {
      this.expand();
    }
  }

  dispose(): void {
    this.toggleButton.remove();
    this.styleElement.remove();

    // Reset all inline styles applied to the GUI DOM element
    this.controlPanel.gui.domElement.style.cssText = '';

    // Ensure the panel is visible (in case it was hidden/collapsed)
    this.controlPanel.gui.domElement.style.display = '';
  }
}
